import { useMemo } from 'react'
import { useAnimationConfig } from '../hooks/useReducedMotion'
import { polarToCartesian } from '../utils/geometry'
import { resolveBadgeRingAngles } from './badgeRingLayout'

/**
 * RadarBadgeRing (bubblenew-v2, T5.1)
 *
 * DOM-Overlay über dem Radar-Canvas: pro Lehrer mit mehreren sichtbaren
 * Datums-Bubbles ein Badge-Button (Lehrer-Code + gefilterte Lektionsanzahl)
 * in einem Ring AUSSERHALB der Radar-Disk — Badges verdecken keine Bubbles.
 *
 * - Offen (Default): Pille mit Code + Anzahl, aria-pressed=false.
 * - Kollabiert: Kreis-Bubble, Durchmesser folgt derselben Flächen-Skala wie
 *   die Canvas-Bubbles (Ø ∝ √n), aria-pressed=true.
 * - Einzellehrer (1 Datum): statisches Chip — ersetzt das OrganicRays-Label.
 * - Klick toggelt den Kollaps (State + Persistenz liegt in AdminRadar).
 * - Keyboard/Screenreader: echte <button>-Elemente; prefers-reduced-motion
 *   deaktiviert die Morph-Transition.
 */

export interface BadgeRingEntry {
  teacherId: string
  code: string
  name: string
  /** Gefilterte, sichtbare Lektionsanzahl (E6) */
  lessonCount: number
  /** Anzahl sichtbarer Datums-Bubbles (≥ 2 => Badge statt Chip) */
  dateCount: number
  angleDeg: number
  collapsed: boolean
}

interface RadarBadgeRingProps {
  entries: BadgeRingEntry[]
  /** Canvas-Grösse (quadratisch) — Ring-Radius = maxRadius + 22 */
  size: number
  onToggle: (teacherId: string) => void
  labels: {
    collapse: string
    expand: string
    lessonCount: (n: number) => string
  }
}

/** Gleiche Flächenskalierung wie calculateVisualRadius('area'): px = 44·√(n/20)·canvasScale. */
function collapsedCirclePx(lessonCount: number, maxRadius: number): number {
  const canvasScale = Math.min(1, maxRadius / 250)
  const px = 44 * canvasScale * Math.sqrt(Math.max(lessonCount, 1) / 20)
  return Math.max(12 * canvasScale, px)
}

function estimateHalfWidthPx(entry: BadgeRingEntry, maxRadius: number, compact: boolean): number {
  if (entry.dateCount <= 1) return compact ? 16 : 20
  if (entry.collapsed) return collapsedCirclePx(entry.lessonCount, maxRadius) / 2
  return compact ? 26 : 38
}

export function RadarBadgeRing({ entries, size, onToggle, labels }: RadarBadgeRingProps) {
  const animConfig = useAnimationConfig()

  const maxRadius = Math.min(size, size) / 2 - 60
  const ringRadius = maxRadius + 22
  // bubblenew-v3 (B3): Kompaktmodus für kleine Viewports — Badges ohne Caret/
  // Zahl-Pill in einer Zeile, damit der Ring bei vielen Lehrern nicht kollidiert.
  const compact = size < 450

  const placed = useMemo(() => {
    const visible = entries.filter(e => e.lessonCount > 0 || e.dateCount > 0)
    const resolved = resolveBadgeRingAngles(
      visible.map(e => ({
        angleDeg: e.angleDeg,
        halfWidthPx: estimateHalfWidthPx(e, maxRadius, compact),
      })),
      ringRadius,
    )
    return visible.map((e, i) => ({
      ...e,
      pos: polarToCartesian(resolved[i] ?? e.angleDeg, ringRadius, size / 2, size / 2),
    }))
  }, [entries, ringRadius, size, maxRadius, compact])

  if (placed.length === 0) return null

  return (
    <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 3 }} aria-label="Radar Lehrer-Badges">
      {placed.map(entry => {
        const isBadge = entry.dateCount > 1 || entry.collapsed
        const collapsedSize = collapsedCirclePx(entry.lessonCount, maxRadius)
        const stateLabel = entry.collapsed ? labels.expand : labels.collapse

        if (!isBadge) {
          // Einzellehrer: statisches Chip (keine Interaktion, kein Kollaps nötig)
          return (
            <span
              key={entry.teacherId}
              className="absolute glass-chip text-gray-700 whitespace-nowrap"
              style={{
                left: entry.pos.x,
                top: entry.pos.y,
                transform: 'translate(-50%, -50%)',
                borderRadius: '12px',
                padding: compact ? '1px 6px' : '2px 8px',
                fontSize: compact ? 10 : 11,
                fontWeight: 600,
              }}
            >
              {entry.code}
            </span>
          )
        }

        return (
          <button
            key={entry.teacherId}
            type="button"
            aria-pressed={entry.collapsed}
            aria-label={`${stateLabel}: ${entry.name} (${entry.code}), ${labels.lessonCount(entry.lessonCount)}`}
            title={`${entry.name} (${entry.code}) — ${labels.lessonCount(entry.lessonCount)} · ${stateLabel}`}
            onClick={() => onToggle(entry.teacherId)}
            className="absolute pointer-events-auto flex items-center justify-center bg-white/95 border-2 border-blue-500 text-gray-800 hover:shadow-[0_0_18px_rgba(59,130,246,0.55)] transition-shadow"
            style={{
              left: entry.pos.x,
              top: entry.pos.y,
              transform: 'translate(-50%, -50%)',
              borderRadius: entry.collapsed ? '50%' : '999px',
              width: entry.collapsed ? collapsedSize : 'auto',
              height: entry.collapsed ? collapsedSize : 'auto',
              minWidth: entry.collapsed ? collapsedSize : undefined,
              padding: entry.collapsed ? 0 : compact ? '2px 7px' : '3px 10px',
              gap: entry.collapsed ? 0 : compact ? 4 : 6,
              flexDirection: entry.collapsed ? 'column' : 'row',
              boxShadow: '0 2px 10px rgba(59,130,246,0.3)',
              fontSize: entry.collapsed ? Math.max(11, collapsedSize * 0.32) : compact ? 11 : 12,
              fontWeight: 800,
              transition: animConfig.reducedMotion
                ? 'none'
                : 'width .4s cubic-bezier(0.34,1.3,0.5,1), height .4s cubic-bezier(0.34,1.3,0.5,1), border-radius .3s',
            }}
          >
            {entry.collapsed ? (
              <>
                <span style={{ fontSize: Math.max(13, collapsedSize * 0.38), lineHeight: 1.05 }}>{entry.lessonCount}</span>
                <span style={{ fontSize: Math.max(8, collapsedSize * 0.18), color: '#64748b', letterSpacing: '0.04em' }}>{entry.code}</span>
              </>
            ) : compact ? (
              <span>{entry.code} · {entry.lessonCount}</span>
            ) : (
              <>
                <span>{entry.code}</span>
                <span
                  className="bg-blue-500 text-white"
                  style={{ borderRadius: 999, padding: '0 6px', fontSize: 11, fontWeight: 800, lineHeight: '17px' }}
                >
                  {entry.lessonCount}
                </span>
                <span style={{ fontSize: 9, color: '#64748b' }} aria-hidden>▼</span>
              </>
            )}
          </button>
        )
      })}
    </div>
  )
}
